import * as React from 'react';
import injectSheet from 'react-jss';
import { Checkbox, message, Drawer, Button, Icon, Tooltip, Select, Spin, Tag } from 'antd';
import { useEffect, useState } from 'react';
import { commonStyles } from 'src/Library/CommonStyles';
import { NAVBAR_HEIGHT } from 'src/globals';
import { CheckboxValueType } from 'antd/lib/checkbox/Group';
import { delinkContainers, searchShippingApi } from 'src/api/shipmentExecution';
import GetExecutionData from 'src/dto/response/getExecution';
import Loader from 'src/components/common/Loader';
import { SEOrgType } from '../../SE.interfaces';
import { APIResponseType } from 'src/Library/Types';
import ApiDto from 'src/dto/ApiDto';

const styles = (theme) => ({
  body: { padding: '16px 24px' },
  childDiv: {
    alignItems: 'center',
    display: 'flex',
    marginBottom: 10,
  },
  leftElement: {
    width: 70,
    marginRight: '16px',
    fontSize: 12,
    fontWeight: 600,
  },
  rightElement: {
    width: '390px',
    fontSize: 12,
  },
  titleDiv: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#111',
  },
  submitButton: {
    fontSize: 12,
    color: '#FFFFFF',
    background: '#006EC3',
    marginLeft: 8,
  },
  columns: {
    padding: 8,
  },
  formItem: {
    marginBottom: 16,
    '& .ant-form-item': {
      marginBottom: 0,
    },
    '& .ant-checkbox-group': {
      ...commonStyles(theme).flexColumn,
    },
  },
  drawer: {
    '& .ant-drawer-header': {
      height: NAVBAR_HEIGHT,
      padding: '13px 24px',
    },
    '& .ant-drawer-body': {
      padding: 0,
    },
  },
  header: {
    borderBottom: '1px solid #ccc',
    display: 'flex',
    justifyContent: 'space-between',
    paddingBottom: 12,
    lineHeight: 1,
  },
  resetButton: {
    color: '#fd1d1d',
    fontSize: 10,
    fontWeight: 600,
    cursor: 'pointer',
  },
  dropDown: { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
});

export type ShipmentSearchDropDown = {
  id: string;
  name: string;
  searchType: string;
  shipmentIdList: string[];
};

abstract class Props {
  classes: {
    [X in keyof ReturnType<typeof styles>]: string;
  };

  selectedShipment: GetExecutionData;

  shipmentContainers: { containerNumber: string; id: string }[];

  viewType: SEOrgType;

  handleCloseDelinkContainers: () => void;

  loadData: () => void;

  loadContainers: () => void;
}

const DelinkContainers = (props: Props) => {
  const {
    handleCloseDelinkContainers,
    classes,
    viewType,
    selectedShipment,
    loadData,
    loadContainers,
    shipmentContainers,
  } = props;

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [selectedContainers, setSelectedContainers] = useState<CheckboxValueType[]>([]);
  const [dropdown, setDropdown] = useState<ShipmentSearchDropDown[]>([]);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [selectedShipmentFromDropdown, setSelectedShipmentFromDropdown] = useState<{
    name: string;
    id: string;
  }>();
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingSubmit, setLoadingSubmit] = useState<boolean>(false);

  const totalPages = 2;

  const handlePrev = () => {
    setCurrentPage(currentPage - 1);
  };

  const handleSubmit = async () => {
    setLoadingSubmit(true);

    const apiBody = {
      containers: shipmentContainers
        .filter((container) => selectedContainers.includes(container.containerNumber))
        .map((container) => {
          return { containerNumber: container.containerNumber, containerId: container.id };
        }),
      shipmentIdToLink: selectedShipmentFromDropdown!.id,
    };

    const response = await delinkContainers(apiBody);

    if (response.isSuccess) {
      message.success('Delink container successful');
      handleCloseDelinkContainers();
      loadData();
      loadContainers();
      return;
    }

    setLoadingSubmit(false);
    message.error(response.errorMessage);
  };

  const handleNext = () => {
    if (currentPage === totalPages) {
      // validateData
      if (!selectedShipmentFromDropdown?.id) {
        message.error('Please select a shipment');
        return;
      }
      if (selectedShipmentFromDropdown.id === selectedShipment.id) {
        message.error('Selected shipment and current shipment should be different');
        return;
      }
      if (!selectedContainers.length) {
        message.error('Please select atleast one container');
        return;
      }
      handleSubmit();
    } else {
      setCurrentPage(currentPage + 1);
    }
  };

  const renderTitle = () => {
    return (
      <div className={classes.titleDiv}>
        <Icon type="close" onClick={handleCloseDelinkContainers} />
        <span className={classes.title} style={{ marginRight: 150 }}>
          Delink Containers
        </span>
        <div style={{ float: 'right' }}>
          <Button
            type="ghost"
            style={{ fontSize: 12, marginRight: '5px' }}
            disabled={currentPage === 1}
            onClick={handlePrev}
          >
            Prev
          </Button>
          <Button
            className={classes.submitButton}
            style={{ marginLeft: '4px' }}
            loading={loadingSubmit}
            onClick={handleNext}
          >
            {currentPage === totalPages ? 'Submit' : 'Next'}
          </Button>
        </div>
      </div>
    );
  };

  const handleResetContainers = () => {
    setSelectedContainers([]);
  };

  const renderContainerSectionHeader = () => {
    return (
      <div
        className={classes.header}
        style={{
          marginBottom: 8,
          marginTop: 10,
        }}
      >
        <div className={classes.title}>Container List</div>
        <div className={classes.resetButton} onClick={handleResetContainers}>
          Reset
        </div>
      </div>
    );
  };

  const renderContainerSectionList = () => {
    return (
      <div className={classes.columns}>
        <Checkbox.Group
          value={selectedContainers}
          onChange={(value) => {
            setSelectedContainers(value);
          }}
        >
          {shipmentContainers.map((container) => (
            <div className={classes.formItem} style={{ marginBottom: 28 }}>
              <Checkbox
                value={container.containerNumber}
                className={classes.title}
                style={{ fontWeight: 500 }}
              >
                {container.containerNumber}
              </Checkbox>
            </div>
          ))}
        </Checkbox.Group>
      </div>
    );
  };

  const renderContainerSection = () => {
    if (currentPage !== 1) {
      return;
    }

    return (
      <div>
        {renderContainerSectionHeader()}
        {renderContainerSectionList()}
      </div>
    );
  };

  const handleSelectedShipment = (search, option) => {
    if (search) {
      setSelectedShipmentFromDropdown({
        name: option.props?.name,
        id: option.props?.shipmentIdList?.[0],
      });
    } else {
      setSelectedShipmentFromDropdown(undefined);
    }
  };

  let timeout: any = null;
  const handleShipmentSearch = (val) => {
    if (val.length) {
      if (timeout) {
        clearTimeout(timeout);
      }
      setIsSearching(true);
      timeout = setTimeout(() => {
        searchShipment(val);
      }, 200);
    } else {
      timeout && clearTimeout(timeout);
      setDropdown([]);
    }
  };

  const searchShipment = async (queryString) => {
    // To Do replace any with typeof ApiDto.SearchShipmentDataDto
    const apiCallObject: any = {
      queryString,
      viewType,
      mode: selectedShipment.mode,
      onlyActiveShipments: true,
    };
    const response: APIResponseType<ShipmentSearchDropDown[]> = await searchShippingApi(
      apiCallObject
    );
    if (response.isSuccess && response.data) {
      setIsSearching(false);
      setDropdown(response.data);
    }
    setIsSearching(false);
  };

  const renderShipmentSearchBar = () => {
    return (
      <div className={classes.childDiv}>
        <span className={classes.leftElement}>Shipment Number</span>
        <div style={{ fontSize: 20, cursor: 'pointer' }}>
          <Tooltip
            placement="top"
            title="Search Shipment / Booking Number / Client Reference Number / Sales Order Number"
            className={classes.rightElement}
          >
            <Select
              showSearch
              allowClear
              dropdownMatchSelectWidth={false}
              notFoundContent={
                isSearching ? (
                  <div style={{ padding: '25px' }}>
                    <Spin />
                  </div>
                ) : (
                  'Not Found'
                )
              }
              suffixIcon={<Icon type="search" />}
              placeholder={'Search'}
              onFocus={() => searchShipment('')}
              onSearch={handleShipmentSearch}
              dropdownMenuStyle={{ width: '390px' }}
              optionLabelProp={'data-value'}
              onChange={handleSelectedShipment}
              defaultValue={selectedShipmentFromDropdown?.name}
            >
              {dropdown?.map((item: any) => (
                <Select.Option {...item} key={item.name} data-value={item.name}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                    }}
                  >
                    <div className={classes.dropDown}>{item.name}</div>
                    <Tag color="cyan">{item.searchType}</Tag>
                  </div>
                </Select.Option>
              ))}
            </Select>
          </Tooltip>
        </div>
      </div>
    );
  };

  const renderSelectedContainers = () => {
    return (
      <>
        <div
          className={classes.header}
          style={{
            marginBottom: 8,
            marginTop: 40,
          }}
        >
          <div className={classes.title}>Selected Containers</div>
        </div>
        {selectedContainers.map((container) => {
          return <div style={{ padding: 8, fontWeight: 'bold' }}>{container}</div>;
        })}
      </>
    );
  };

  const renderShipmentSearchBody = () => {
    return (
      <>
        {renderShipmentSearchBar()}
        {renderSelectedContainers()}
      </>
    );
  };

  const renderSearchShipmentHeader = () => {
    return (
      <div
        className={classes.header}
        style={{
          marginBottom: 8,
          marginTop: 20,
        }}
      >
        <div className={classes.title}>Search Shipment</div>
      </div>
    );
  };

  const renderShipmentSearchSection = () => {
    if (currentPage !== totalPages) {
      return;
    }
    return (
      <>
        {renderSearchShipmentHeader()}
        {renderShipmentSearchBody()}
      </>
    );
  };

  const renderBody = () => {
    return (
      <div className={classes.body}>
        {renderContainerSection()}
        {renderShipmentSearchSection()}
      </div>
    );
  };

  return (
    <Drawer
      width="519.7px"
      height="650px"
      visible
      title={renderTitle()}
      closable={false}
      className={classes.drawer}
    >
      {loading && <Loader zIndex={1} />}
      {renderBody()}
    </Drawer>
  );
};

export default injectSheet(styles)(DelinkContainers);
